import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Stack,
  Paper,
  Tooltip
} from '@mui/material';
import { 
  Delete, 
  AddCircleOutline, 
  Kitchen,
  Receipt,
  Save,
  Close,
  Edit,
  ArrowBack as ArrowBackIcon,
  Monitor,
  Tv,
  PointOfSale
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAdvanceInfo, setAdvanceInfo } from '../../hooks/apiHandler';
import { useAuth } from '../../hooks/useAuth';


/**
 * AdvanceSettingDevices Component
 * Provides a professional management interface for Terminal-specific hardware 
 * and interface settings, integrated with the Miira POS backend.
 */
export const AdvanceSettingDevices = () => {
  const { state } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMdOrLg = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { Logout } = useAuth();
  
  const [editMode, setEditMode] = useState(false);
  const [layout, setLayout] = useState([]);
  const [clsList, setClsList] = useState([]);
  const [tksList, setTksList] = useState([]);
  const [printerOptions, setPrinterOptions] = useState([]);
  const [categoryOptions, setCatg] = useState(["Final Receipt", "All Categories"]);

  const [advset, setAdvset] = useState({
    layout: "0",
    cls: "0",
    tks: "0",
    drawerRecptEnabled: false,
    recipPrinterName: '',
    kitchPrint: [{ printerName: '', categories: [] }],
  });

  // Fetch data on mount
  useEffect(() => {
    async function calladv() {
      try {
        const response = await getAdvanceInfo({ storeID: state?.storeid, deviceid: state?.id }, Logout);

        if (response?.data) {
          const data = response.data;
          
          // Map layout names
          setLayout(data.layout || []);

          // Extract category strings and append (avoiding duplicates)
          const fetchedCats = (data.catgorys || []).map((cat) => cat.catgory);
          setCatg((prev) => [
            ...new Set([...prev, ...fetchedCats])
          ]);

          // Update current settings
          if (data.deviceAdvSetting && Object.keys(data.deviceAdvSetting).length > 0) {
            setAdvset(data.deviceAdvSetting);
          }
          
          setPrinterOptions(data.listofStorePrinter || []);
          setClsList(data.listCLS || []);
          setTksList(data.listTKS || []);
        }
      } catch (err) {
        console.error('Failed to fetch advance info:', err);
      }
    }

    calladv();
  }, [state, Logout]);

  // Save updated settings
  const handleSave = async () => {
    try {
      const resp = await setAdvanceInfo({ storeID: state?.storeid, deviceid: state?.id, advset }, Logout);
      if (resp?.data) {
        setAdvset(resp.data);
      }
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  return (
    <Box sx={{ width: isMdOrLg ? '80%' : '100%', mx: 'auto', p: isMobile ? 2 : 4 }}>
      
      {/* Header Bar */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <IconButton 
              onClick={() => navigate("/admin/settings?tab=2")}
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider',
                '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="800" color="text.primary">
              {state?.name || "Terminal Settings"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {state?.id} | Device hardware and interface routing
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={1.5}>
              {editMode ? (
                <>
                  <Button 
                    variant="outlined" 
                    color="inherit" 
                    startIcon={<Close />} 
                    onClick={() => setEditMode(false)}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<Save />} 
                    onClick={handleSave}
                    sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<Edit />} 
                  onClick={() => setEditMode(true)}
                  sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                >
                  Edit Configuration
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        
        {/* Section 1: UI Layout & Interface */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 4 }}>
            <CardHeader 
              title="Interface Settings" 
              avatar={<PointOfSale color="primary" />}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
            />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Primary Layout</InputLabel>
                  <Select
                    label="Primary Layout"
                    value={advset.layout}
                    onChange={(e) => setAdvset((prev) => ({ ...prev, layout: e.target.value }))}
                    disabled={!editMode}
                  >
                    <MenuItem value="0">None</MenuItem>
                    {layout.map((item, idx) => (
                      <MenuItem key={idx} value={item.planName}>{item.planName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Client Facing Screen</InputLabel>
                  <Select
                    label="Client Facing Screen"
                    value={advset.cls?._id || "0"}
                    onChange={
                      (e) => {
                        var selected = clsList.find(c => c._id === e.target.value);
                        console.log("Selected CLS:", selected);
                        if (selected) {
                          setAdvset((prev) => ({ ...prev, cls: selected }));
                        }else{
                          setAdvset((prev) => ({ ...prev, cls: "0" }));
                        }
                        
                        // setAdvset((prev) => ({ ...prev, cls: e.target.value }))
                      }}
                    disabled={!editMode}
                    startAdornment={<Monitor sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />}
                  >
                    <MenuItem value="0">None</MenuItem>
                    {clsList.map((item, idx) => (
                      (item.sta === 0 && <MenuItem key={idx} value={item._id}>{item.name}</MenuItem>)
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Token Status Screen</InputLabel>
                  <Select
                    label="Token Status Screen"
                    value={advset.tks?._id || "0"}
                    onChange={
                      (e) => {
                        var selected = tksList.find(t => t._id === e.target.value);
                        console.log("Selected TKS:", selected);
                        if (selected) {
                          setAdvset((prev) => ({ ...prev, tks: selected }));
                        }else{
                          setAdvset((prev) => ({ ...prev, tks: "0" }));
                        }
                        
                        // setAdvset((prev) => ({ ...prev, tks: e.target.value }))
                      }}
                    disabled={!editMode}
                    startAdornment={<Tv sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />}
                  >
                    <MenuItem value="0">None</MenuItem>
                    {tksList.map((item, idx) => (
                      (item.sta === 0 && <MenuItem key={idx} value={item._id}>{item.name}</MenuItem>)
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Section 2: Receipt Hardware */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 4 }}>
            <CardHeader 
              title="Receipt Printing" 
              avatar={<Receipt color="primary" />}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
            />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block', textTransform: 'uppercase' }}>
                    Final Receipt (Sales)
                  </Typography>
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      value={advset.recipPrinterName}
                      onChange={(e) => setAdvset((prev) => ({ ...prev, recipPrinterName: e.target.value }))}
                      disabled={!editMode}
                    >
                      <MenuItem value="">None</MenuItem>
                      {printerOptions.map((p) => (
                        <MenuItem key={p._id} value={p.name}>{p.name || 'Unnamed Printer'}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderStyle: 'dashed', borderRadius: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={advset.drawerRecptEnabled}
                        onChange={(e) => setAdvset((prev) => ({ ...prev, drawerRecptEnabled: e.target.checked }))}
                        disabled={!editMode}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" fontWeight="bold">Drawer Receipts</Typography>
                        <Typography variant="caption" color="text.secondary">Trigger cash drawer on final receipt print</Typography>
                      </Box>
                    }
                  />
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Section 3: Kitchen Routing */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
            <CardHeader 
              title="Kitchen Receipts & KDS" 
              avatar={<Kitchen color="primary" />}
              action={
                <Button
                  startIcon={<AddCircleOutline />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (advset.kitchPrint.length < 5) {
                      setAdvset((prev) => ({
                        ...prev,
                        kitchPrint: [...prev.kitchPrint, { printerName: '', categories: [] }],
                      }));
                    }
                  }}
                  disabled={!editMode || advset.kitchPrint.length >= 5}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Add Routing Rule
                </Button>
              }
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
            />
            <Divider />
            <CardContent sx={{ bgcolor: '#fafafa', p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                {advset.kitchPrint.map((kp, index) => (
                  <Paper 
                    key={index} 
                    variant="outlined" 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      bgcolor: 'background.paper',
                      transition: '0.2s',
                      '&:hover': { borderColor: 'primary.main', boxShadow: theme.shadows[1] }
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Station Printer</InputLabel>
                          <Select
                            label="Station Printer"
                            value={kp.printerName}
                            onChange={(e) =>
                              setAdvset((prev) => {
                                const updated = [...prev.kitchPrint];
                                updated[index].printerName = e.target.value;
                                return { ...prev, kitchPrint: updated };
                              })
                            }
                            disabled={!editMode}
                          >
                            <MenuItem value="">None</MenuItem>
                            {printerOptions.map((p) => (
                              ((p.sta === 0 || !p.sta) && <MenuItem key={p._id} value={p.name}>{p.name || 'Unnamed Printer'}</MenuItem>)
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={categoryOptions}
                          value={kp.categories}
                          onChange={(_, newValue) =>
                            setAdvset((prev) => {
                              const updated = [...prev.kitchPrint];
                              updated[index].categories = newValue;
                              return { ...prev, kitchPrint: updated };
                            })
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Assigned Categories" placeholder="Select" />
                          )}
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                        {editMode && (
                          <Tooltip title="Delete Rule">
                            <IconButton 
                              color="error" 
                              onClick={() =>
                                setAdvset((prev) => {
                                  const updated = [...prev.kitchPrint];
                                  updated.splice(index, 1);
                                  return { ...prev, kitchPrint: updated };
                                })
                              }
                              sx={{ border: '1px solid', borderColor: 'error.light' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                {advset.kitchPrint.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      No kitchen routing rules configured.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdvanceSettingDevices;